import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { formatCurrency as formatCurrencyUtil, Moneda } from '../utils/currency';
import { formatDate as formatDateUtil, formatTime as formatTimeUtil, formatDateTime as formatDateTimeUtil, type FormatoFecha, type FormatoHora } from '../utils/dateFormat';
import { useAuth } from './AuthContext';
import { API_URL } from '../config';

export type FormatoImpresionFactura = 'carta' | 'voucher58';

/** Paleta de colores: tema UI, degradados y colores de impresión */
export interface PaletaConfig {
  primaryMain?: string;
  primaryLight?: string;
  primaryDark?: string;
  secondaryMain?: string;
  secondaryLight?: string;
  secondaryDark?: string;
  backgroundDefault?: string;
  backgroundPaper?: string;
  gradientLogin?: string;
  gradientCard?: string;
  gradientSidebar?: string;
  gradientPageBg?: string;
  printHeaderColor?: string;
  printTableHeaderColor?: string;
  printAccentColor?: string;
}

interface ConfigState {
  moneda: Moneda;
  nombre?: string;
  direccion?: string;
  telefono?: string;
  ciudad?: string;
  ruc?: string;
  logo_url?: string;
  formato_impresion_factura?: FormatoImpresionFactura;
  formato_descarga_pdf_factura?: FormatoImpresionFactura;
  zona_horaria?: string;
  formato_fecha?: FormatoFecha;
  formato_hora?: FormatoHora;
  paleta?: PaletaConfig | null;
  whatsapp_telefono?: string;
  whatsapp_texto?: string;
  rate_limit_login_max?: number | null;
  restringir_toma_muestra_solo_su_ubicacion?: number;
  inventario_habilitado?: boolean;
  portal_base_url?: string | null;
  etiquetas_modo_tubos?: 'por_examen' | 'por_tipo_tubo' | 'unica';
  etiquetas_gap_mm?: number;
}

interface ConfigContextType {
  config: ConfigState | null;
  moneda: Moneda;
  formatCurrency: (value: number | undefined | null) => string;
  formatDate: (value: string | Date | null | undefined) => string;
  formatTime: (value: string | Date | null | undefined) => string;
  formatDateTime: (value: string | Date | null | undefined) => string;
  loadConfig: () => Promise<void>;
}

const defaultMoneda: Moneda = 'USD';

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

export const ConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { token } = useAuth();
  const [config, setConfig] = useState<ConfigState | null>(null);

  const loadConfig = useCallback(async () => {
    const t = localStorage.getItem('token');
    if (!t) {
      try {
        const res = await axios.get(`${API_URL}/configuracion/public`);
        const data = res.data || {};
        setConfig({
          moneda: defaultMoneda,
          nombre: data.nombre,
          logo_url: data.logo_url,
          paleta: data.paleta && typeof data.paleta === 'object' ? data.paleta : undefined,
        });
      } catch {
        setConfig({ moneda: defaultMoneda });
      }
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/configuracion`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      const moneda = res.data?.moneda === 'NIO' ? 'NIO' : 'USD';
      setConfig({
        moneda,
        nombre: res.data?.nombre,
        direccion: res.data?.direccion,
        telefono: res.data?.telefono,
        ciudad: res.data?.ciudad,
        ruc: res.data?.ruc,
        logo_url: res.data?.logo_url,
        formato_impresion_factura: res.data?.formato_impresion_factura === 'voucher58' ? 'voucher58' : 'carta',
        formato_descarga_pdf_factura: res.data?.formato_descarga_pdf_factura === 'voucher58' ? 'voucher58' : 'carta',
        zona_horaria: res.data?.zona_horaria || 'America/Managua',
        formato_fecha: (res.data?.formato_fecha === 'MM/DD/YYYY' || res.data?.formato_fecha === 'YYYY-MM-DD') ? res.data.formato_fecha : 'DD/MM/YYYY',
        formato_hora: res.data?.formato_hora === '12' ? '12' : '24',
        paleta: res.data?.paleta && typeof res.data.paleta === 'object' ? res.data.paleta : undefined,
        whatsapp_telefono: res.data?.whatsapp_telefono || '',
        whatsapp_texto: res.data?.whatsapp_texto || '',
        rate_limit_login_max: res.data?.rate_limit_login_max != null ? Number(res.data.rate_limit_login_max) : null,
        restringir_toma_muestra_solo_su_ubicacion: res.data?.restringir_toma_muestra_solo_su_ubicacion ? 1 : 0,
        inventario_habilitado: !!res.data?.inventario_habilitado,
        portal_base_url: res.data?.portal_base_url ?? null,
        etiquetas_modo_tubos: (() => {
          const m = String(res.data?.etiquetas_modo_tubos || 'por_examen').toLowerCase();
          if (m === 'por_tipo_tubo') return 'por_tipo_tubo' as const;
          if (m === 'unica' || m === 'etiqueta_unica') return 'unica' as const;
          return 'por_examen' as const;
        })(),
        etiquetas_gap_mm:
          res.data?.etiquetas_gap_mm != null && Number.isFinite(Number(res.data.etiquetas_gap_mm))
            ? Math.max(0, Math.min(30, Number(res.data.etiquetas_gap_mm)))
            : 3,
      });
    } catch (e) {
      setConfig({ moneda: defaultMoneda });
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [token, loadConfig]);

  const moneda: Moneda = config?.moneda ?? defaultMoneda;
  const formatCurrency = useCallback(
    (value: number | undefined | null) => formatCurrencyUtil(value, moneda),
    [moneda]
  );
  const zonaHoraria = config?.zona_horaria ?? 'America/Managua';
  const formatoFecha = (config?.formato_fecha === 'MM/DD/YYYY' || config?.formato_fecha === 'YYYY-MM-DD') ? config.formato_fecha : 'DD/MM/YYYY';
  const formatoHora = config?.formato_hora === '12' ? '12' : '24';
  const formatDate = useCallback(
    (value: string | Date | null | undefined) => formatDateUtil(value, zonaHoraria, formatoFecha),
    [zonaHoraria, formatoFecha]
  );
  const formatTime = useCallback(
    (value: string | Date | null | undefined) => formatTimeUtil(value, zonaHoraria, formatoHora),
    [zonaHoraria, formatoHora]
  );
  const formatDateTime = useCallback(
    (value: string | Date | null | undefined) => formatDateTimeUtil(value, zonaHoraria, formatoFecha, formatoHora),
    [zonaHoraria, formatoFecha, formatoHora]
  );

  return (
    <ConfigContext.Provider value={{ config, moneda, formatCurrency, formatDate, formatTime, formatDateTime, loadConfig }}>
      {children}
    </ConfigContext.Provider>
  );
};

export const useConfig = () => {
  const ctx = useContext(ConfigContext);
  if (ctx === undefined) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return ctx;
};
